import { useQuery } from "@tanstack/react-query";
import { useMemo, type ReactNode } from "react";
import { portalFetch } from "../api/http";
import { BheliLoader } from "../ui/brand";
import { ChatIcon } from "../ui/icons/Icon";
import { teamPrefixes, teamSharedKeys, text, type TeamUi } from "../team/teamText";
import { FeedbackScope, MenuScope, TeamContext } from "../team/TeamUi";

// Piezas comunes de Configuración de la empresa y Usuarios (/portal/configuracion y /portal/configuracion/usuarios), con
// el sistema de Chat › Equipo y Bandejas (team.css y team/TeamUi): encabezado C03, tabla C28, fila C17, formulario C05,
// tarjetas de opción C08, interruptor C09, toast C13 y sin permiso C40. Así se ven y se usan igual (V15). Los permisos los
// decide el servidor (/api/portal/settings); la pantalla solo los refleja.

/** Textos de Configuración y Usuarios: los de Equipo más los de la empresa, invitaciones y el alta. */
export const usersPrefixes = [
  ...teamPrefixes,
  ...teamSharedKeys,
  "USERS_",
  "SETTINGS_",
  "ORGROLE_",
  "INVITE_",
  "INV_",
  "ADD_",
  "TEAM_",
  "REG_FIRST",
  "REG_LAST",
  "CHATCFG_TITLE",
  "CHATCFG_HINT",
  "CP_ACTION_UNAVAILABLE",
];

const theme = (): "light" | "dark" =>
  document.documentElement.getAttribute("data-theme") === "dark" ? "dark" : "light";

/** El contexto de las piezas de Equipo con los textos de Usuarios (una sola lectura de textos por idioma). */
export function useUsersUi(locale: string): TeamUi | null {
  const texts = useQuery({
    queryKey: ["texts", locale, "settings-users"],
    queryFn: () =>
      portalFetch<Record<string, string>>(
        `/api/portal/texts/${locale}?prefixes=${encodeURIComponent(usersPrefixes.join(","))}`,
      ),
  });
  return useMemo<TeamUi | null>(
    () =>
      texts.data ? { labels: texts.data, locale, timeZone: "UTC", userId: 0, organizationId: 0, theme: theme() } : null,
    [texts.data, locale],
  );
}

/** El marco de cada pantalla: cargador de arranque, contexto de Equipo, un solo toast y un menú ⋮ abierto a la vez. */
export function UsersFrame({ ui, ready, children }: { ui: TeamUi; ready: boolean; children: ReactNode }) {
  return (
    <TeamContext.Provider value={ui}>
      <section className="cfg1-activation">
        <div className="cfg1-startup">
          <BheliLoader label={text(ui, "BRAND_LOADING")} theme={ui.theme} />
        </div>
        {ready ? (
          <div className="tm cfg1-live tm-users" data-floating-lane="scroll">
            <FeedbackScope undoLabel={text(ui, "CFG1_R1_UNDO")}>
              <MenuScope>{children}</MenuScope>
            </FeedbackScope>
          </div>
        ) : null}
      </section>
    </TeamContext.Provider>
  );
}

/** Encabezado C03: ← a la pantalla de arriba, título y subtítulo opcional; las acciones van como hijos. */
export function UsersHead({
  ui,
  title,
  sub,
  backHref,
  backLabelKey,
  children,
}: {
  ui: TeamUi;
  title: string;
  sub?: string;
  backHref: string;
  backLabelKey: string;
  children?: ReactNode;
}) {
  return (
    <header className="tm-head" data-part="encabezado">
      <div className="tm-head-title">
        <a
          className="tm-back"
          href={backHref}
          aria-label={text(ui, backLabelKey)}
          title={text(ui, backLabelKey)}
          data-part="atras"
        >
          <ChatIcon name="arrow-left" />
        </a>
        <div>
          <h1>{title}</h1>
          {sub ? <p className="tm-head-sub">{sub}</p> : null}
        </div>
      </div>
      {children ? <div className="tm-head-actions">{children}</div> : null}
    </header>
  );
}

/**
 * Sin permiso (C40): estado de página con el motivo y la salida. Usuarios es solo para dueño y administrador; quien no lo
 * es vuelve a Configuración.
 */
export function UsersDenied({ ui, title }: { ui: TeamUi; title: string }) {
  return (
    <>
      <UsersHead ui={ui} title={title} backHref="/portal/configuracion" backLabelKey="SETTINGS_TITLE" />
      <div className="tm-denied" role="status" data-part="sin-permiso">
        <strong>{text(ui, "USERS_DENIED_TITLE")}</strong>
        <span>{text(ui, "USERS_DENIED_HINT")}</span>
        <a className="tm-btn secondary" href="/portal/configuracion">
          {text(ui, "USERS_BACK_TO_SETTINGS")}
        </a>
      </div>
    </>
  );
}

/** Aviso en línea C14 de error, con «Reintentar» si se puede repetir la lectura. */
export function UsersError({
  message,
  retryLabel,
  onRetry,
}: {
  message: string;
  retryLabel?: string;
  onRetry?: () => void;
}) {
  return (
    <div className="tm-notice error" role="alert" data-part="error">
      <ChatIcon name="circle-alert" />
      <span>
        {message}{" "}
        {onRetry && retryLabel ? (
          <button type="button" className="tm-link" onClick={onRetry}>
            {retryLabel}
          </button>
        ) : null}
      </span>
    </div>
  );
}

/** Nombre del rol de empresa con las palabras de Equipo (Dueño, Administrador, Miembro). */
export const roleName = (ui: TeamUi, role: string) =>
  text(ui, role === "ORG_OWNER" ? "ORGROLE_OWNER" : role === "ORG_ADMIN" ? "ORGROLE_ADMIN" : "ORGROLE_MEMBER");

/** Copia al portapapeles; si el navegador no lo permite, false (la pantalla lo dice y el dato sigue a la vista). */
export async function copyText(value: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(value);
    return true;
  } catch {
    return false;
  }
}
